import * as THREE from 'three';
import type { WindowRect } from '@/xr/worldMask';

/**
 * Derives the MR window rectangle from the room itself: WebXR plane detection
 * (Quest Space Setup) exposes the walls and ceiling as planes, and the window
 * the virtual range is seen through should end exactly where they are — left
 * and right edges on the side walls, top edge on the ceiling, the plane of the
 * window on the far wall the player is aiming through.
 *
 * Everything is resolved in the ANCHOR frame (tee position + aim yaw), the
 * same basis WorldMask masks in; +lat is the player's LEFT facing downrange,
 * matching the mask's lat-axis convention.
 *
 * Planes can arrive several frames after session start (and after each Space
 * Setup change), so `solve` is designed to be polled every frame until it
 * returns a rect. It never throws on missing capability — a session without
 * 'plane-detection' (or a room without Space Setup) simply never resolves,
 * and the app stays on the infinite portal.
 */

/** Sanity clamps — a "wall" outside these is furniture or a mis-detection. */
const MIN_DIST = 1.2;
const MAX_DIST = 8;
const MIN_SIDE = 0.8;
const MAX_SIDE = 8;
const MIN_TOP = 1.8;
const MAX_TOP = 4;
/** How vertical a wall normal / horizontal a ceiling must be. */
const WALL_NORMAL_MAX_Y = 0.3;
/** cos of the max angle between a side wall's normal and the lateral axis. */
const SIDE_ALIGN_COS = 0.7; // ~45°
/** cos of the max angle between the far wall's normal and the aim line. */
const FAR_ALIGN_COS = 0.7;

export class RoomBounds {
  // scratch
  #normal = new THREE.Vector3();
  #point = new THREE.Vector3();
  #fwd = new THREE.Vector2();
  #lat = new THREE.Vector2();

  /**
   * One attempt at resolving the window from this frame's detected planes.
   * Returns undefined until a ceiling, both side walls AND a far wall have all
   * been seen — partial rooms fall back to the infinite portal cleanly.
   */
  solve(
    frame: XRFrame,
    referenceSpace: XRReferenceSpace,
    anchor: { position: THREE.Vector3; yaw: number },
  ): WindowRect | undefined {
    const planes: Set<any> | undefined = (frame as any).detectedPlanes;
    if (!planes || planes.size === 0) return undefined;

    this.#fwd.set(Math.sin(anchor.yaw), Math.cos(anchor.yaw));
    // +lat = player's left when facing downrange (mask convention)
    this.#lat.set(this.#fwd.y, -this.#fwd.x);

    let top: number | undefined;
    let left: number | undefined;
    let right: number | undefined;
    let dist: number | undefined;

    for (const plane of planes) {
      const pose = frame.getPose(plane.planeSpace, referenceSpace);
      if (!pose) continue;

      const p = pose.transform.position;
      const o = pose.transform.orientation;
      // XRPlane convention: the plane lies in its space's XZ, +Y is the normal
      this.#normal.set(0, 1, 0).applyQuaternion(
        new THREE.Quaternion(o.x, o.y, o.z, o.w)
      );
      this.#point.set(p.x, p.y, p.z);

      if (plane.orientation === 'horizontal' || Math.abs(this.#normal.y) > 0.85) {
        // Ceiling: a horizontal plane comfortably above standing head height
        const height = this.#point.y - anchor.position.y;
        if (height >= MIN_TOP && height <= MAX_TOP) {
          top = top === undefined ? height : Math.min(top, height);
        }
        continue;
      }
      if (Math.abs(this.#normal.y) > WALL_NORMAL_MAX_Y) continue; // slanted — ignore

      // Vertical plane: classify by how its normal relates to the aim frame.
      const relX = this.#point.x - anchor.position.x;
      const relZ = this.#point.z - anchor.position.z;
      const pointFwd = relX * this.#fwd.x + relZ * this.#fwd.y;
      const pointLat = relX * this.#lat.x + relZ * this.#lat.y;
      const normalFwd = this.#normal.x * this.#fwd.x + this.#normal.z * this.#fwd.y;
      const normalLat = this.#normal.x * this.#lat.x + this.#normal.z * this.#lat.y;

      if (Math.abs(normalLat) >= SIDE_ALIGN_COS) {
        // Side wall. Perpendicular distance from the anchor to the wall plane
        // along the lateral axis (walls aren't perfectly parallel to the aim —
        // use the plane equation, not the centre point).
        const perp = Math.abs(relX * this.#normal.x + relZ * this.#normal.z)
          / Math.max(Math.abs(normalLat), 1e-3);
        if (perp < MIN_SIDE || perp > MAX_SIDE) continue;
        if (pointLat > 0) {
          left = left === undefined ? perp : Math.min(left, perp);
        } else {
          right = right === undefined ? perp : Math.min(right, perp);
        }
      } else if (Math.abs(normalFwd) >= FAR_ALIGN_COS && pointFwd > MIN_DIST) {
        // Far wall: faces back along the aim line, downrange of the tee
        const perp = Math.abs(relX * this.#normal.x + relZ * this.#normal.z)
          / Math.max(Math.abs(normalFwd), 1e-3);
        if (perp < MIN_DIST || perp > MAX_DIST) continue;
        dist = dist === undefined ? perp : Math.min(dist, perp);
      }
    }

    if (top === undefined || left === undefined || right === undefined || dist === undefined) {
      return undefined;
    }
    return { dist, left, right, top };
  }
}
